import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { planHasCompliance } from '../utils/userAccess';
import {
  fetchOnboardingProgress,
  markMilestoneComplete,
  setCompletedAt as persistCompletedAt,
} from '../utils/onboardingProgress';

// ─── Animations ────────────────────────────────────────────────────────────────

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const fadeCheck = keyframes`
  0%   { transform: scale(0) rotate(-10deg); opacity: 0; }
  60%  { transform: scale(1.2) rotate(3deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(233, 112, 36, 0.4); }
  50%       { box-shadow: 0 0 0 6px rgba(233, 112, 36, 0); }
`;

// ─── Styled Components ─────────────────────────────────────────────────────────

const Wrap = styled.div`
  position: fixed;
  bottom: 80px;         /* sits above the bell: 24px bottom + 48px button + 8px gap */
  left: 24px;
  z-index: 7900;        /* below BellWrap z-index 8500 */
  font-family: 'Poppins', system-ui, sans-serif;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 0.875rem 0.5rem 0.625rem;
  border-radius: 24px;
  border: 1.5px solid rgba(233,112,36,0.25);
  background: #fff;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12), 0 1px 3px rgba(233,112,36,0.1);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.15), 0 2px 6px rgba(233,112,36,0.15);
    border-color: rgba(233,112,36,0.4);
  }
  &:active { transform: translateY(0); }
`;

const TriggerIconWrap = styled.div`
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; color: #fff;
  background: ${p => p.$allDone ? '#16a34a' : '#e97024'};
  transition: background 0.4s ease;
  ${p => !p.$allDone && css`animation: ${pulse} 3s ease-in-out infinite;`}
`;

const TriggerLabel = styled.span`
  font-size: 0.78rem; font-weight: 600; color: #1a1a1a; white-space: nowrap;
`;

const TriggerCount = styled.span`
  font-size: 0.7rem; font-weight: 700;
  color: ${p => p.$allDone ? '#16a34a' : '#e97024'};
`;

const ChevronIcon = styled.span`
  display: flex; align-items: center; color: #9ca3af;
  transition: transform 0.2s ease;
  transform: ${p => p.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
  margin-left: 2px;
`;

const Panel = styled.div`
  position: absolute;
  bottom: calc(100% + 10px);
  left: 0; width: 320px;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 16px;
  box-shadow: 0 -4px 24px rgba(0,0,0,0.08), 0 8px 32px rgba(0,0,0,0.12);
  overflow: hidden;
  animation: ${slideUp} 0.22s cubic-bezier(0.34,1.4,0.64,1) forwards;
`;

const PanelHeader = styled.div`
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid rgba(0,0,0,0.06);
`;

const PanelHeadRow = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between;
  margin-bottom: 0.625rem;
`;

const PanelTitle = styled.div`
  font-size: 0.82rem; font-weight: 700; color: #1a1a1a; letter-spacing: -0.01em;
`;

const PanelSubtitle = styled.div`
  font-size: 0.7rem; color: #9ca3af; margin-top: 0.1rem; font-weight: 400;
`;

const CloseBtn = styled.button`
  width: 22px; height: 22px; border-radius: 6px; border: none; background: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #9ca3af; padding: 0; transition: all 0.15s;
  &:hover { background: rgba(0,0,0,0.06); color: #374151; }
`;

const ProgressLabel = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 0.35rem;
`;

const ProgressText = styled.span`
  font-size: 0.67rem; font-weight: 600;
  color: ${p => p.$allDone ? '#16a34a' : '#e97024'};
`;

const ProgressTrack = styled.div`
  height: 4px; border-radius: 4px; background: rgba(0,0,0,0.06); overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%; border-radius: 4px;
  background: ${p => p.$allDone ? '#16a34a' : '#e97024'};
  width: ${p => p.$pct}%;
  transition: width 0.5s cubic-bezier(0.4,0,0.2,1), background 0.4s ease;
`;

const ItemList = styled.div`
  padding: 0.5rem; display: flex; flex-direction: column; gap: 2px;
  max-height: 340px; overflow-y: auto;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 4px; }
`;

const Item = styled.div`
  display: flex; align-items: center; gap: 0.625rem;
  padding: 0.6rem 0.625rem; border-radius: 10px;
  cursor: default;
  opacity: ${p => p.$done ? 0.5 : 1};
  transition: background 0.15s;
`;

const CheckBox = styled.div`
  width: 22px; height: 22px; border-radius: 6px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s ease;
  ${p => p.$done ? css`
    background: #e97024; border: 1.5px solid #e97024; color: #fff;
    svg { animation: ${fadeCheck} 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards; }
  ` : css`
    border: 1.5px solid rgba(0,0,0,0.18); background: transparent;
  `}
`;

const ItemLabel = styled.span`
  font-size: 0.775rem; font-weight: ${p => p.$done ? 500 : 600}; flex: 1;
  color: ${p => p.$done ? '#9ca3af' : '#1a1a1a'};
  text-decoration: ${p => p.$done ? 'line-through' : 'none'};
  text-decoration-color: rgba(156,163,175,0.5);
  transition: all 0.3s ease; letter-spacing: -0.01em;
`;

const CompleteBanner = styled.div`
  margin: 0.5rem; padding: 0.75rem; border-radius: 10px;
  background: #f0fdf4; border: 1px solid rgba(34,197,94,0.2);
  display: flex; align-items: center; gap: 0.5rem;
`;

const BannerText = styled.div`font-size: 0.75rem; font-weight: 600; color: #166534;`;
const BannerSub = styled.div`font-size: 0.67rem; color: rgba(22,101,52,0.7); margin-top: 0.1rem;`;

const PanelFooter = styled.div`
  padding: 0.5rem 0.75rem 0.75rem;
  border-top: 1px solid rgba(0,0,0,0.06);
`;

const ResumeBtn = styled.button`
  width: 100%;
  display: flex; align-items: center; justify-content: center; gap: 0.4rem;
  padding: 0.6rem 0.875rem; border-radius: 10px;
  border: 1.5px solid rgba(233,112,36,0.3); background: #fff; color: #e97024;
  font-family: inherit; font-size: 0.775rem; font-weight: 700; letter-spacing: -0.01em;
  cursor: pointer; transition: all 0.15s ease;
  &:hover { background: #e97024; color: #fff; border-color: #e97024; }
  &:active { transform: translateY(1px); }
`;

// ─── Icons ─────────────────────────────────────────────────────────────────────

const CheckMarkIcon = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
  </svg>
);
const RocketIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.82m5.84-2.56a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.82m2.56-5.84a14.98 14.98 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
  </svg>
);
const StarIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
  </svg>
);
const ChevronDown = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7"/>
  </svg>
);
const XSmall = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12"/>
  </svg>
);
const PlayIcon = () => (
  <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

// ─── Checklist Config ──────────────────────────────────────────────────────────

const CHECKLIST_ITEMS = [
  { id: 'folder',   label: 'Link a project folder',          event: 'tour_folder_linked'    },
  { id: 'research', label: 'Try Research',                      event: 'tour_research_used'    },
  { id: 'settings', label: 'Update Project Settings',           event: 'tour_settings_opened'  },
  { id: 'register', label: 'Explore the Requirements Register', event: 'tour_register_viewed'  },
  { id: 'scan',     label: 'Run your first scan',               event: 'tour_scan_initiated'   },
];

const HIDE_AFTER_DAYS = 7;

// ─── PostHog Helpers ───────────────────────────────────────────────────────────

/**
 * TODO (Sprint 9 — Shivani):
 * Replace this stub with a call to a Lambda proxy that queries the PostHog
 * Query API for historical event data.
 *
 * Why a proxy is required:
 *   - The PostHog Query API (/api/environments/:id/query/) is a PRIVATE endpoint.
 *   - It requires a PERSONAL API key with `query:read` scope — NOT the project key.
 *   - Personal API keys must never be exposed client-side.
 *
 * Suggested approach:
 *   1. Create a personal PostHog API key with only `query:read` scope.
 *   2. Store it in AWS Secrets Manager.
 *   3. Add a Lambda (via API Gateway, Cognito-authenticated) that:
 *        - Accepts the user's PostHog distinct_id
 *        - Calls POST https://us.i.posthog.com/api/environments/:env_id/query/
 *          with an EventsQuery filtering to CHECKLIST_ITEMS events for that distinct_id
 *        - Also reads the `onboarding_completed_at` person property (set below)
 *        - Returns: { completedEvents: string[], completedAt: string | null }
 *   4. Replace the stub below with: fetch('/api/checklist-status', { ... })
 *
 * INTERIM: until the Lambda is wired up, progress is persisted client-side in
 * localStorage (see onboardingProgress.js), so the checklist reflects real
 * progress and the 7-day hide window is enforceable across refreshes on the
 * same browser. The Lambda will replace this with cross-device persistence.
 * Live session tracking (the posthog.capture interception below) still layers
 * on top for instant in-session updates.
 */
async function fetchChecklistStateFromPostHog(userId) {
  const { events, completedAt } = await fetchOnboardingProgress(userId);
  return { completedEvents: events, completedAt };
}

/**
 * Called the first time all items are checked off in a live session.
 * Persists the completion timestamp locally (so the 7-day hide window survives
 * refreshes) and sets a PostHog person property so the future Lambda can return
 * completedAt for cross-device enforcement.
 */
function recordCompletionTimestamp(userId) {
  persistCompletedAt(userId, new Date());
  window.posthog?.people?.set?.({
    onboarding_completed_at: new Date().toISOString(),
  });
}

function isWithinHideWindow(completedAt) {
  if (!completedAt) return false;
  const msElapsed = Date.now() - completedAt.getTime();
  return msElapsed >= HIDE_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function OnboardingChecklist({ planKey, onResumeTour, userId } = {}) {
  // Tier-dependent items: the "Run your first scan" step is Studio/Firm only.
  const items = useMemo(
    () => CHECKLIST_ITEMS.filter((i) => i.id !== 'scan' || planHasCompliance(planKey)),
    [planKey]
  );

  // Open by default so a freshly-logged-in user sees what's left to do without
  // having to discover/click the collapsed pill first. Still closable (CloseBtn,
  // click-outside) — closing it doesn't get re-forced open later this session.
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(new Set());
  const [completedAt, setCompletedAt] = useState(null); // Date | null
  const [hidden, setHidden] = useState(false);
  const wrapRef = useRef(null);
  const completionRecordedRef = useRef(false);
  const autoCollapsedRef = useRef(false);

  // Load historical state on mount. Keyed by userId (auth.user?.sub), passed
  // explicitly from App.js rather than read from PostHog's distinct_id — see
  // onboardingProgress.js header for why distinct_id is unreliable this early
  // after sign-in.
  useEffect(() => {
    let cancelled = false;
    fetchChecklistStateFromPostHog(userId).then(({ completedEvents, completedAt: ts }) => {
      if (!cancelled) {
        setCompleted(completedEvents);
        setCompletedAt(ts);
        // If already completed and 7 days have passed, hide immediately
        if (ts && isWithinHideWindow(ts)) setHidden(true);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [userId]);

  // Intercept posthog.capture for live-session updates
  useEffect(() => {
    const ph = window.posthog;
    if (!ph?.capture) return;

    const tourEventSet = new Set(CHECKLIST_ITEMS.map(i => i.event));
    const originalCapture = ph.capture.bind(ph);

    ph.capture = function (event, ...args) {
      if (tourEventSet.has(event)) {
        markMilestoneComplete(event, userId);
        setCompleted(prev => {
          const next = new Set(prev);
          next.add(event);
          return next;
        });
      }
      return originalCapture(event, ...args);
    };

    return () => { ph.capture = originalCapture; };
  }, [userId]);

  const completedCount = items.filter(i => completed.has(i.event)).length;
  const total = items.length;
  const allDone = completedCount === total;
  const pct = Math.round((completedCount / total) * 100);

  // When all items first become complete in this session:
  // 1. Record the timestamp as a PostHog person property
  // 2. Auto-collapse the panel
  // 3. Schedule hide after 7 days (approximated here as a timeout for the
  //    remainder of the session; the real enforcement happens via completedAt
  //    on next load once the Lambda is wired up)
  useEffect(() => {
    if (!allDone) return;

    if (!completionRecordedRef.current) {
      completionRecordedRef.current = true;
      const now = new Date();
      setCompletedAt(now);
      recordCompletionTimestamp(userId);
    }

    if (open && !autoCollapsedRef.current) {
      autoCollapsedRef.current = true;
      const collapseTimer = setTimeout(() => setOpen(false), 1800);
      return () => clearTimeout(collapseTimer);
    }
  }, [allDone, open, userId]);

  // Click-outside to close
  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Don't render anything if hidden (past the 7-day window)
  if (hidden) return null;

  return (
    <Wrap ref={wrapRef}>
      {open && (
        <Panel>
          <PanelHeader>
            <PanelHeadRow>
              <div>
                <PanelTitle>Getting started</PanelTitle>
                <PanelSubtitle>Complete these steps to set up CODiii</PanelSubtitle>
              </div>
              <CloseBtn onClick={() => setOpen(false)} title="Close">
                <XSmall />
              </CloseBtn>
            </PanelHeadRow>
            <ProgressLabel>
              <ProgressText $allDone={allDone}>
                {allDone ? '🎉 All done!' : `${completedCount} of ${total} complete`}
              </ProgressText>
              <ProgressText $allDone={allDone}>{pct}%</ProgressText>
            </ProgressLabel>
            <ProgressTrack>
              <ProgressFill $pct={pct} $allDone={allDone} />
            </ProgressTrack>
          </PanelHeader>

          {allDone ? (
            <>
              <CompleteBanner>
                <StarIcon style={{ color: '#22c55e', flexShrink: 0 }} />
                <div>
                  <BannerText>You're all set!</BannerText>
                  <BannerSub>CODiii is fully configured and ready to use.</BannerSub>
                </div>
              </CompleteBanner>
              <ItemList>
                {items.map(item => (
                  <Item key={item.id} $done>
                    <CheckBox $done><CheckMarkIcon /></CheckBox>
                    <ItemLabel $done>{item.label}</ItemLabel>
                  </Item>
                ))}
              </ItemList>
            </>
          ) : (
            <ItemList>
              {items.map(item => {
                const done = completed.has(item.event);
                return (
                  <Item key={item.id} $done={done}>
                    <CheckBox $done={done}>{done && <CheckMarkIcon />}</CheckBox>
                    <ItemLabel $done={done}>{item.label}</ItemLabel>
                  </Item>
                );
              })}
            </ItemList>
          )}

          {onResumeTour && (
            <PanelFooter>
              <ResumeBtn
                onClick={() => { setOpen(false); onResumeTour(); }}
                title={allDone ? 'Restart the guided tour' : 'Resume the guided tour where you left off'}
              >
                <PlayIcon />
                {allDone ? 'Restart tour' : 'Resume tour'}
              </ResumeBtn>
            </PanelFooter>
          )}
        </Panel>
      )}

      <Trigger
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        title="Getting started checklist"
      >
        <TriggerIconWrap $allDone={allDone}>
          {allDone ? <StarIcon /> : <RocketIcon />}
        </TriggerIconWrap>
        <TriggerLabel>{allDone ? 'Setup complete' : 'Getting started'}</TriggerLabel>
        <TriggerCount $allDone={allDone}>
          {loading ? '…' : `${completedCount}/${total}`}
        </TriggerCount>
        <ChevronIcon $open={open}><ChevronDown /></ChevronIcon>
      </Trigger>
    </Wrap>
  );
}
